//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import angular, { IScope } from 'angular';
import React, { FC, useRef } from 'react';
import styled from 'styled-components';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import VirtualizedContentCardGrid from '@components/organisms/content-card-grid/VirtualizedContentCardGrid';
import { UploaderDisclaimer } from '@components/uploader-disclaimer/UploaderDisclaimer';
import { QueuedUpload } from '@legacy-components/common/queued-upload/QueuedUpload';
import makeAngularReactControllerWithScope, {
  applyAsyncScope,
} from '@react-bridge/angular-react-widget-with-scope';
import { Mixins } from '@theme/mixins';
import { itemToPartialCuid } from '@utils/partialCuidUtils';

import { LibraryGridCard } from 'app/components/content/library-grid-content/LibraryGridCard';
import {
  GRID_CONTENT_CARD_HEIGHT,
  GRID_CONTENT_CARD_HEIGHT_NO_METADATA,
  GRID_CONTENT_CARD_WIDTH,
  ViewMode,
} from 'app/components/content/library-grid-content/constants';

interface IOwnProps {
  items: IContentLibraryItem[];
  isRefreshingContent: boolean;
  fetchMoreContent: () => void;
  scrollContainer: string;
  viewMode: ViewMode;
  endOfItemsReached: boolean;
  isLoadingInitialContent: boolean;
  queuedUploads?: string[];
}

const renderItem = (item: IContentLibraryItem, viewMode: ViewMode) => {
  if (item) {
    return (
      <LibraryGridCard
        key={item.id}
        item={item}
        disableMetadata={viewMode === 'grid'}
      />
    );
  } else {
    return null;
  }
};

const renderLoadingItem = (uuid: string) => {
  return (
    <LoadingSheet key={uuid}>
      <QueuedUpload uuid={uuid} />
    </LoadingSheet>
  );
};

export const LibraryGridContent: FC<IOwnProps> = ({
  items,
  isRefreshingContent,
  fetchMoreContent,
  scrollContainer,
  viewMode,
  endOfItemsReached,
  isLoadingInitialContent,
  queuedUploads,
}) => {
  const cardHeght =
    viewMode === 'grid'
      ? GRID_CONTENT_CARD_HEIGHT_NO_METADATA
      : GRID_CONTENT_CARD_HEIGHT;

  const containerRef = useRef<HTMLDivElement | null>(null);

  const orderedPartialCuids = items.map((item) =>
    itemToPartialCuid(item.itemType, item.id),
  );

  if (isLoadingInitialContent) {
    return <CenteredSpinner />;
  }

  return (
    <LibraryGridContentContainer ref={containerRef}>
      <VirtualizedContentCardGrid
        cardHeight={cardHeght}
        cardWidth={GRID_CONTENT_CARD_WIDTH}
        isRefreshingContent={isRefreshingContent}
        includeCardPadding={true}
        scrollContainerSelector={scrollContainer}
        onBottomReached={fetchMoreContent}
        postFixConfig={{
          shouldShowPostFixComponent: true,
          postFixComponent: endOfItemsReached ? (
            <VirtualizedContentCardGrid.EndOfContent />
          ) : (
            <VirtualizedContentCardGrid.LoadingSpinner />
          ),
        }}
        scrollContainerRef={containerRef}
        orderedPartialCuids={orderedPartialCuids}
      >
        {queuedUploads?.map((uuid) => renderLoadingItem(uuid))}
        {items.map((item) => renderItem(item, viewMode))}
      </VirtualizedContentCardGrid>
      <UploaderDisclaimer />
    </LibraryGridContentContainer>
  );
};

angular.module('curalate').component('libraryGridContent', {
  bindings: {
    queuedUploads: '<',
    endOfItemsReached: '<',
    items: '<',
    scrollContainer: '<',
    fetchMoreContent: '<',
    isRefreshingContent: '<',
    viewMode: '<',
    isLoadingInitialContent: '<',
  },
  controller: makeAngularReactControllerWithScope(
    (scope: { $ctrl: IOwnProps } & IScope) => {
      return (
        <LibraryGridContent
          endOfItemsReached={scope.$ctrl.endOfItemsReached}
          items={scope.$ctrl.items}
          isRefreshingContent={scope.$ctrl.isRefreshingContent}
          fetchMoreContent={applyAsyncScope(
            scope,
            scope.$ctrl.fetchMoreContent,
          )}
          scrollContainer={scope.$ctrl.scrollContainer}
          viewMode={scope.$ctrl.viewMode}
          isLoadingInitialContent={scope.$ctrl.isLoadingInitialContent}
          queuedUploads={scope.$ctrl.queuedUploads}
        />
      );
    },
  ),
});

const LibraryGridContentContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const LoadingSheet = styled.div`
  ${Mixins.Sheet.Low}

  height: ${GRID_CONTENT_CARD_HEIGHT}px;
  width: ${GRID_CONTENT_CARD_WIDTH}px;
`;
